import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
} from 'react';
import { JobSearchContext as Context } from './ContextCreator';
import * as services from './utils/jobSearchUtils';
import { JOB_SEARCH_FILTER_TYPES } from '../components/Includes/Constants/jobSearch';
import { buildQueryString } from '../components/Utilities/UrlFunctions';
import { useDebouncedEffect } from './utils/utils';
import { handleFiringSearchEmitterOnSearchResult } from '../components/JobV3/searchEmitters';
import { SnowplowSearchTracker } from '../components/Analytics/Snowplow/SnowplowSearchTracker';

// all api calls should be abstracted to a utils file
// import * as service from './utils'

export const JOB_CARD_LOADING = 'LOADING';
export const JOBS_NOT_FOUND = 'JOBS_NOT_FOUND';

const JobSearchProvider = ({
    children,
    _content,
    _activeFilters,
    _filters,
    _page,
    _jobSearch,
    baseUrl,
    jobUrl,
    jobAlerts,
    isAuthenticatedUser,
    jobsTotal,
    searchIndexName,
    _searchQueryId,
    pageLimit,
    CTAJobSearch,
}) => {
    const [searchQueryId, setSearchQueryId] = useState(_searchQueryId);
    const [content, setContent] = useState(_content);
    const [filters, setFilters] = useState(_filters);
    const [activeFilters, setActiveFilters] = useState(_activeFilters);
    const [fullSearchQueryId, setFullSearchQueryId] = useState(
        `${_searchQueryId}${_activeFilters?.company_id || ''}${
            _activeFilters?.locations || ''
        }${_activeFilters?.remote}${_activeFilters?.company_size || ''}`
    );
    const [changedFilter, setChangedFilter] = useState('');
    const [totalJobs, setTotalJobs] = useState(jobsTotal);
    const [jobsSearch, setJobsSearch] = useState({
        keywords: _jobSearch.jobSearchTerm,
        location: _jobSearch.jobLocation,
        lng: _jobSearch.jobLng,
        lat: _jobSearch.jobLat,
    });
    const [singleJobUrl, setSingleJobUrl] = useState(jobUrl);
    const [singleJobDetail, setSingleJobDetail] = useState(
        jobsTotal ? JOB_CARD_LOADING : JOBS_NOT_FOUND
    );
    const [page, setPage] = useState(Number(_page) || 1);
    const [jobAlert, setJobAlert] = useState(false);
    const [jobNotifications, setJobNotifications] = useState(jobAlerts);
    const [isMobileJobCardOpen, setIsMobileJobCardOpen] = useState(false);
    const [isDirectApplyOpen, setIsDirectApplyOpen] = useState(false);
    const [hasApplied, setHasApplied] = useState(false);
    const [selectFirstJob, setSelectFirstJob] = useState(false);
    const [resetFilters, setResetFilters] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [previousSearchId, setPreviousSearchId] = useState(null);

    useEffect(() => {
        if (isMounted && document) {
            const _title = `${jobsSearch?.keywords} jobs in ${
                jobsSearch?.location.length ? jobsSearch?.location : 'Anywhere'
            } | Fairygodboss`;
            document.title = _title;
        }
    }, [isMounted, jobsSearch]);

    useEffect(() => {
        setIsMounted(true);
        const searchFilters = [];
        activeFilters[JOB_SEARCH_FILTER_TYPES.COMPANY]?.forEach((company) => {
            searchFilters.push({
                filter_name: 'Company Id',
                filter_value: company,
            });
        });
        if (jobsSearch.location) {
            searchFilters.push({
                filter_name: 'Location',
                filter_value: jobsSearch.location,
            });
        }
        activeFilters[JOB_SEARCH_FILTER_TYPES.COMPANY_SIZE]?.forEach(
            (companySize) => {
                searchFilters.push({
                    filter_name: 'Company Size',
                    filter_value: companySize,
                });
            }
        );
        const searchId = `${searchQueryId}${fullSearchQueryId}`;
        SnowplowSearchTracker({
            searchId,
            searchKeyword: jobsSearch.keywords,
            searchFilters,
            isRemote: !!activeFilters.remote[0],
            callback: () => setPreviousSearchId(searchId),
        });
    }, []);

    // initial mount
    useEffect(() => {
        if (!jobUrl?.length && _content.length) {
            const _firstJob = _content[0];
            let companyUrl = _firstJob?.company?.url.split('/');
            companyUrl = companyUrl[companyUrl.length - 1];
            const slug = _firstJob?.hash;
            const url = `${companyUrl}/${slug}`;
            setSingleJobUrl(url);
        }
    }, [jobUrl, _content]);

    // get job saved status for list
    useEffect(() => {
        if (content?.length) {
            const firstJob = content[0];
            const hasSavedJobs = Object.prototype.hasOwnProperty.call(
                firstJob,
                'userLiked'
            );
            if (!hasSavedJobs) {
                if (isAuthenticatedUser) {
                    services.getCommunityContent({ content }).then((data) => {
                        setContent(data);
                    });
                } else {
                    setContent((prevContent) => {
                        const _prev = prevContent.map((c) => ({
                            ...c,
                            userLiked: false,
                        }));
                        return _prev;
                    });
                }
            }
        }
    }, [content, isAuthenticatedUser]);

    // get job saved status for single job
    useEffect(() => {
        if (singleJobDetail?.jobDetails) {
            const hasSavedJobDetail = Object.prototype.hasOwnProperty.call(
                singleJobDetail,
                'userLiked'
            );
            if (!hasSavedJobDetail && isAuthenticatedUser) {
                services
                    .getCommunityContent({
                        content: [{ hash: singleJobDetail.jobDetails?.slug }],
                    })
                    .then((data) => {
                        const userLiked = data[0]?.userLiked;
                        setSingleJobDetail({
                            ...singleJobDetail,
                            userLiked,
                        });
                    });
            }
        }
    }, [singleJobDetail, isAuthenticatedUser]);

    // prevent rerenders when changing the job search url
    const singleJobUrlRef = useRef();

    // mobile query job page handler
    const mobileJobOpenRef = useRef();

    const [ctsNameMap, setCtsNameMap] = useState(null);

    useEffect(() => {
        if (content.length) {
            const ctsMap = {};
            content.forEach((c) => {
                ctsMap[c.hash] = c.ctsName;
            });
            setCtsNameMap(ctsMap);
        }
    }, [content]);

    // update CTS cookie
    useEffect(() => {
        const slug = singleJobDetail?.jobDetails?.slug;
        if (searchQueryId && slug) {
            const ctsName = ctsNameMap?.[slug];
            if (ctsName) {
                handleFiringSearchEmitterOnSearchResult({
                    queryID: searchQueryId,
                    hash: slug,
                    ctsName,
                });
            }
        }
    }, [searchQueryId, singleJobDetail, ctsNameMap]);

    const jobSearchRef = useRef(_searchQueryId);

    useEffect(() => {
        jobSearchRef.current = searchQueryId;
    }, [searchQueryId]);

    /**
     * Function to compose search query url
     */
    const buildSearchUrl = useCallback(
        /**
         * @param {string} _baseUrl Url string to prepend query string with
         * @returns {string}
         */
        (_baseUrl) => {
            const { keywords, location, lng, lat } = jobsSearch;
            const url = `${_baseUrl}`;
            const queryParams = {};
            const encodedCompanyFilters =
                activeFilters &&
                encodeURIComponent(activeFilters.company_id.join('+'));
            const encodedCompanySizeFilters =
                activeFilters &&
                encodeURIComponent(
                    activeFilters.company_size.map(encodeURIComponent).join('+')
                );
            const encodedLocationFilters =
                activeFilters &&
                encodeURIComponent(activeFilters.locations.join('+'));
            const remoteFilter =
                (activeFilters && activeFilters.remote[0]) || 0;

            if (keywords) {
                queryParams.keywords = encodeURIComponent(keywords);
            }
            if (location) {
                queryParams.location = location;
            }

            if (lat && lng) {
                queryParams.lat = lat;
                queryParams.lng = lng;
            }

            if (jobSearchRef.current && _baseUrl === services.API_SEARCH_JOBS) {
                queryParams.searchId = encodeURIComponent(jobSearchRef.current);
            }

            if (page) {
                // HACK: If we are using the API, pages start at 0 and not 1, offset page
                if (_baseUrl === services.API_SEARCH_JOBS) {
                    queryParams.page = page - 1;
                } else {
                    queryParams.page = page;
                }
            }

            if (mobileJobOpenRef?.current) {
                queryParams.jobOpen = mobileJobOpenRef?.current?.isOpen;
            }

            if (singleJobUrlRef?.current) {
                const encodedJobUrl = encodeURIComponent(
                    singleJobUrlRef?.current
                );
                queryParams.jobPage = encodedJobUrl;
            }

            queryParams.filters_remote = remoteFilter;
            queryParams.filters_cmp = encodedCompanyFilters;
            queryParams.filters_cmp_size = encodedCompanySizeFilters;
            queryParams.filters_loc = encodedLocationFilters;
            const query = buildQueryString(queryParams);
            return url + query;
        },
        [activeFilters, jobsSearch, page]
    );

    useEffect(() => {
        if (!mobileJobOpenRef?.current) {
            const windowUrl = window.location.search;
            const params = new URLSearchParams(windowUrl);
            const _isOpen = params?.get('jobOpen')?.toLowerCase() === 'true';
            mobileJobOpenRef.current = { isOpen: _isOpen };
            if (_isOpen) {
                setIsMobileJobCardOpen(true);
            }
        } else {
            mobileJobOpenRef.current = { isOpen: isMobileJobCardOpen };
            requestAnimationFrame(() => {
                const browserUrl = buildSearchUrl(baseUrl);

                window.history.pushState(
                    { isMobileOpen: isMobileJobCardOpen },
                    '',
                    browserUrl
                );
            });
        }
    }, [isMobileJobCardOpen, baseUrl, buildSearchUrl]);

    // handle pushState pop for mobile jobs card
    useEffect(() => {
        const isJobOpen = (e) => {
            setIsMobileJobCardOpen(!!e?.state?.isMobileOpen);
        };
        // initiate the event handler
        window.addEventListener('popstate', isJobOpen);

        // this will clean up the event every time the component is re-rendered
        return function cleanup() {
            window.removeEventListener('popstate', isJobOpen);
        };
    }, []);

    /**
     * Function to search for single job page data
     */
    const searchSingleJob = useCallback(
        /**
         * @param  {Object} SingleJobSearchParams
         * @param {string} SingleJobSearchParams.companyUrl Company URL for job search
         * @param {string} SingleJobSearchParams.slug Job Slug id
         * @returns {Promise<void>}
         */
        async ({ url }) => {
            setSingleJobUrl(url);
            setSingleJobDetail(JOB_CARD_LOADING);
            const jobDetails = await services.searchSingleJob({
                url,
            });
            setSingleJobDetail(jobDetails);
        },
        []
    );

    useEffect(() => {
        if (singleJobUrl) {
            searchSingleJob({ url: singleJobUrl });
        }
    }, [singleJobUrl, searchSingleJob]);

    useDebouncedEffect(
        () => {
            if (!isMounted) {
                return;
            }
            const API_URL = buildSearchUrl(services.API_SEARCH_JOBS);
            const browserUrl = buildSearchUrl(baseUrl);
            services
                .searchJobs(API_URL)
                .then(({ jobs, facets, total, queryID }) => {
                    setContent(jobs);
                    if (resetFilters) {
                        setFilters(facets);
                        setResetFilters(false);
                        setChangedFilter('');
                    } else {
                        const facetsToUpdate = { ...filters };
                        Object.keys(facets).forEach((key) => {
                            if (changedFilter !== key)
                                facetsToUpdate[key] = facets[key];
                        });
                        Object.keys(JOB_SEARCH_FILTER_TYPES).forEach((key) => {
                            const value = JOB_SEARCH_FILTER_TYPES[key];
                            if (!(value in facets) && changedFilter !== value) {
                                facetsToUpdate[value] = [];
                            }
                        });
                        setFilters(facetsToUpdate);
                        setChangedFilter('');
                    }
                    setTotalJobs(total);
                    setSearchQueryId(queryID);
                    const updatedFullQuery = `${queryID}${
                        activeFilters?.company_id || ''
                    }${activeFilters?.locations || ''}${activeFilters?.remote}${
                        activeFilters?.company_size || ''
                    }`;
                    setFullSearchQueryId(updatedFullQuery);
                    const searchFilters = [];
                    activeFilters[JOB_SEARCH_FILTER_TYPES.COMPANY]?.forEach(
                        (company) => {
                            searchFilters.push({
                                filter_name: 'Company Id',
                                filter_value: company,
                            });
                        }
                    );
                    if (jobsSearch.location) {
                        searchFilters.push({
                            filter_name: 'Location',
                            filter_value: jobsSearch.location || 'Anywhere',
                        });
                    }
                    activeFilters[
                        JOB_SEARCH_FILTER_TYPES.COMPANY_SIZE
                    ]?.forEach((companySize) => {
                        searchFilters.push({
                            filter_name: 'Company Size',
                            filter_value: companySize,
                        });
                    });

                    if (previousSearchId !== updatedFullQuery) {
                        SnowplowSearchTracker({
                            searchId: updatedFullQuery,
                            searchKeyword: jobsSearch.keywords,
                            searchFilters,
                            isRemote: !!activeFilters.remote[0],
                            callback: () =>
                                setPreviousSearchId(updatedFullQuery),
                        });
                    }
                    window.history.replaceState({}, '', browserUrl);

                    if (selectFirstJob) {
                        if (jobs.length) {
                            const _firstJob = jobs[0];
                            let companyUrl = _firstJob?.company?.url.split('/');
                            companyUrl = companyUrl[companyUrl.length - 1];
                            const slug = _firstJob?.hash;
                            const url = `${companyUrl}/${slug}`;
                            setSingleJobUrl(url);
                        } else {
                            setSingleJobDetail(JOBS_NOT_FOUND);
                            setSingleJobUrl(null);
                        }
                        setSelectFirstJob(false);
                    }
                });
        },
        [baseUrl, buildSearchUrl],
        100
    );

    useEffect(() => {
        singleJobUrlRef.current = singleJobUrl;
        const browserUrl = buildSearchUrl(baseUrl);
        window.history.replaceState({}, '', browserUrl);
    }, [singleJobUrl, buildSearchUrl, baseUrl]);

    /**
     * Function to filter job results
     */
    const filterJobs = useCallback(
        /**
         * @param {Object} FilterJobsParams
         * @param {string} FilterJobsParams.type type of filter to apply
         * @param {Object} FilterJobsParams.filter
         * @param {string} FilterJobsParams.filter.value value of filter to apply
         */
        ({ type, filter }) => {
            setPage(1);
            setSelectFirstJob(true);
            setChangedFilter(type);
            setActiveFilters((prevActiveFilter) => {
                let newActiveFilters = {
                    [JOB_SEARCH_FILTER_TYPES.COMPANY]: [],
                    [JOB_SEARCH_FILTER_TYPES.COMPANY_SIZE]: [],
                    [JOB_SEARCH_FILTER_TYPES.LOCATION]: [],
                    [JOB_SEARCH_FILTER_TYPES.REMOTE]: [],
                };
                if (Object.values(JOB_SEARCH_FILTER_TYPES).includes(type)) {
                    if (prevActiveFilter[type].includes(filter.value)) {
                        newActiveFilters = {
                            ...prevActiveFilter,
                            [type]: prevActiveFilter[type].filter(
                                (activeFilter) => activeFilter !== filter.value
                            ),
                        };
                    } else {
                        newActiveFilters = {
                            ...prevActiveFilter,
                            [type]: [...prevActiveFilter[type], filter.value],
                        };
                    }
                }
                return newActiveFilters;
            });
        },
        []
    );

    /**
     * Function to filter job results
     */
    const setFilterByType = useCallback(
        /**
         * @param {Object} FilterJobsParams
         * @param {string} FilterJobsParams.type type of filter to apply
         * @param {string[]} FilterJobsParams.values value of filter to apply
         */
        ({ type, values }) => {
            setPage(1);
            setSelectFirstJob(true);
            setActiveFilters((prevActiveFilter) => {
                let newActiveFilters = {
                    [JOB_SEARCH_FILTER_TYPES.COMPANY]: [],
                    [JOB_SEARCH_FILTER_TYPES.COMPANY_SIZE]: [],
                    [JOB_SEARCH_FILTER_TYPES.LOCATION]: [],
                    [JOB_SEARCH_FILTER_TYPES.REMOTE]: [],
                };
                if (Object.values(JOB_SEARCH_FILTER_TYPES).includes(type)) {
                    if (prevActiveFilter[type].length < values.length)
                        setChangedFilter(type);
                    newActiveFilters = {
                        ...prevActiveFilter,
                        [type]: values,
                    };
                }
                return newActiveFilters;
            });
        },
        []
    );

    /**
     * Function to clear all filters
     */
    const clearFilters = useCallback(() => {
        setSelectFirstJob(true);
        setActiveFilters({
            company_id: [],
            company_size: [],
            locations: [],
            remote: [],
        });
    }, []);

    /**
     * Add job alert for user
     */
    const followJobAlert = useCallback(
        /**
         * @param {Object} FollowJobAlertParams
         * @param {string} FollowJobAlertParams.jobSearchTerm Job search
         * @param {string} FollowJobAlertParams.jobLocation Location string
         * @param {number} FollowJobAlertParams.jobLng lattitude
         * @param {number} FollowJobAlertParams.jobLat longitude
         */
        ({ jobSearchTerm, jobLocation, jobLng, jobLat }) => {
            const jobAlertData = {
                jobSearchTerm,
                jobLocation,
                jobLng,
                jobLat,
            };

            services.followJobAlert(jobAlertData).then((res) => {
                jobAlertData.jobNotificationId = res.data.id;
                const { notificationCount, notifications } = jobNotifications;

                const _jobNotifications = {
                    notificationCount,
                    notifications,
                };
                _jobNotifications.notificationCount += 1;
                _jobNotifications.notifications = [
                    ..._jobNotifications.notifications,
                    jobAlertData,
                ];
                setJobNotifications(_jobNotifications);
            });
        },
        [setJobNotifications, jobNotifications]
    );

    /**
     * Remove current job alert
     */
    const unfollowJobAlert = useCallback(
        /**
         * @param {Object} UnfollowAlertParams
         * @param {string} UnfollowAlertParams.notificationId Id to remove
         */
        ({ notificationId }) => {
            services
                .unfollowJobAlert({
                    notificationId,
                })
                .then(() => {
                    const {
                        notificationCount,
                        notifications,
                    } = jobNotifications;
                    const _jobNotifications = {
                        notificationCount,
                        notifications,
                    };
                    _jobNotifications.notificationCount -= 1;
                    _jobNotifications.notifications = notifications.filter(
                        (n) => n.jobNotificationId !== notificationId
                    );
                    setJobNotifications(_jobNotifications);
                });
        },
        [jobNotifications]
    );

    /**
     * On job alert toggle click handler
     */
    const onAlterToggleClick = useCallback(() => {
        const { keywords, location, lat, lng } = jobsSearch;
        if (!jobAlert) {
            followJobAlert({
                jobSearchTerm: keywords,
                jobLocation: location,
                jobLng: lng,
                jobLat: lat,
            });
        } else if (jobAlert?.jobNotificationId) {
            unfollowJobAlert({
                notificationId: jobAlert.jobNotificationId,
            });
        }
    }, [jobsSearch, jobAlert, followJobAlert, unfollowJobAlert]);

    /**
     * Function to check if current job search has an alert
     * @returns {boolean}
     */
    const isJobAlertSaved = useCallback(() => {
        if (!jobNotifications) {
            return;
        }
        const { notifications } = jobNotifications;
        if (notifications) {
            const { keywords, location } = jobsSearch;
            const match = notifications.find(
                (j) =>
                    j.jobSearchTerm?.toLowerCase() ===
                        keywords?.toLowerCase() &&
                    j.jobLocation?.toLowerCase() === location?.toLowerCase()
            );
            return match;
        }
        return false;
    }, [jobNotifications, jobsSearch]);

    /**
     * On mount check to see if current job search is saved
     */
    useEffect(() => {
        const jobAlertMatch = isJobAlertSaved();
        setJobAlert(jobAlertMatch);
    }, [isJobAlertSaved]);

    /**
     * Toggle job save status for user
     */
    const onJobSaveClick = useCallback(
        /**
         * @param {Object} OnJobSaveParams
         * @param {string} OnJobSaveParams.userLiked has user saved this job
         * @param {string} OnJobSaveParams.hash job slug
         */
        ({ userLiked, hash }) => {
            services
                .toggleJobSave({
                    userLiked,
                    hash,
                })
                .then(() => {
                    // update if single jobs page
                    if (singleJobDetail?.jobDetails?.slug === hash) {
                        setSingleJobDetail((prevState) => ({
                            ...prevState,
                            userLiked: !userLiked,
                        }));
                    }

                    setContent((prevState) => {
                        // update in content
                        const index = prevState.findIndex(
                            (c) => c.hash === hash
                        );
                        if (index > -1) {
                            const newContent = [...prevState];
                            newContent[index].userLiked = !userLiked;
                            return newContent;
                        }
                        return prevState;
                    });
                });
        },
        [singleJobDetail, setContent, services.toggleJobSave]
    );

    return (
        <Context.Provider
            value={{
                singleJobUrl,
                onJobSaveClick: isAuthenticatedUser ? onJobSaveClick : () => {},
                fullSearchQueryId,
                jobNotifications,
                jobAlert,
                setJobNotifications,
                onAlterToggleClick,
                content,
                filterJobs,
                filters,
                activeFilters,
                setJobsSearch,
                jobsSearch,
                searchSingleJob,
                singleJobDetail,
                isDirectApplyOpen,
                hasApplied,
                setHasApplied,
                setIsDirectApplyOpen,
                page,
                setPage,
                totalJobs,
                clearFilters,
                setSingleJobUrl,
                isAuthenticatedUser,
                setIsMobileJobCardOpen,
                isMobileJobCardOpen,
                setFilterByType,
                setSelectFirstJob,
                searchQueryId,
                searchIndexName,
                setResetFilters,
                pageLimit,
                setSearchQueryId,
                CTAJobSearch: !isAuthenticatedUser && CTAJobSearch,
            }}
        >
            {children}
        </Context.Provider>
    );
};

export const useJobSearchContext = () => useContext(Context);

export default JobSearchProvider;
